import { Injectable } from '@angular/core';
import { Logger, LoggerFactory } from '../core/logger.service';
import {
  ActionOnComponent,
  ButtonActionType,
  LinkedState,
  StatesInUse,
  Widget,
  WidgetComponent,
  WidgetComponentSubType,
  WidgetComponentType,
  WidgetContainer,
  WidgetState,
} from './widgets.model';
import { WidgetComponentsService } from './widget-components.service';
import WidgetComponentHelpers from './widget-component-helpers';
import { WidgetEditorDataService } from './widget-editor-data.service';
import { WidgetService } from 'app/shared/services/widget.service';

@Injectable({ providedIn: 'root' })
export class WidgetStatesService {
  private readonly logger: Logger;

  constructor(
    readonly loggerFactory: LoggerFactory,
    private readonly widgetEditorDataService: WidgetEditorDataService,
    private readonly widgetService: WidgetService,
  ) {
    this.logger = loggerFactory.get('WidgetStatesService');
  }

  public static getStateByComponentId(states: WidgetState[], targetComponentId: number): WidgetState {
    if (!states) {
      return undefined;
    }
    for (const state of states) {
      const component = WidgetComponentsService.getComponentById(state, targetComponentId);
      if (component) {
        return state; // When a component is found we know which state it belongs to
      }
    }
    return undefined;
  }

  public static getStateByContainerId(states: WidgetState[], containerId: number): WidgetState {
    if (!states) {
      return undefined;
    }
    for (const state of states) {
      const container = state.containers.some((x) => x.id === containerId);
      if (container) {
        return state;
      }
      if (state.floatingContainer?.id === containerId) {
        return state;
      }
    }
    return undefined;
  }

  private getStateAndComponentByComponentId(states, targetComponentId): any {
    let targetComponent: WidgetComponent;
    let targetContainer: any;

    const targetState = WidgetStatesService.getStateByComponentId(states, targetComponentId);

    if (targetState) {
      // Check normal containers
      for (const container of targetState.containers || []) {
        if (container.components) {
          for (const component of container.components) {
            if (component.id === targetComponentId) {
              targetComponent = component;
              targetContainer = container;
            }
            if (component.children) {
              const child = component.children.find((child) => child.id === targetComponentId);
              if (child) {
                targetComponent = child;
                targetContainer = container;
              }
            }
          }
        }
      }

      // Check floating container
      if (targetState.floatingContainer) {
        const container = targetState.floatingContainer;
        if (container.components) {
          for (const component of container.components) {
            if (component.id === targetComponentId) {
              targetComponent = component;
              targetContainer = container;
            }
          }
        }
      }
    }

    return { targetState, targetComponent, targetContainer };
  }

  public checkIfStateIsInUse(widget: Widget, stateId: number, submitStateUsed?): boolean {
    if (widget && widget.states) {
      for (const state of widget.states) {
        const stateCheckResult = this.checkIdUsage(state, stateId, submitStateUsed);
        if (stateCheckResult) {
          return true;
        }

        if (state.containers) {
          for (const container of state.containers) {
            if (this.checkUsageOnContainer(container, stateId, submitStateUsed)) {
              return true;
            }
          }
        }
        if (state.floatingContainer && state.floatingContainer.components) {
          if (this.checkUsageOnContainer(state.floatingContainer, stateId, submitStateUsed)) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private checkUsageOnContainer(container: WidgetContainer, stateId: number, submitStateUsed?): boolean {
    if (container.components) {
      for (const component of container.components) {
        if (component) {
          const result = this.checkIdUsage(component, stateId, submitStateUsed);
          if (result) {
            return true;
          }
          if (component.children) {
            for (const childComponent of component.children) {
              const childResult = this.checkIdUsage(childComponent, stateId, submitStateUsed);
              if (childResult) {
                return true;
              }
            }
          }
        }
      }
    }
    return false;
  }

  private checkIdUsage(component: any, stateId: number, submitStateUsed?): boolean {
    if (component && component.alternativeStepId === stateId) {
      return true;
    }

    if (
      (component && component.actionType === ButtonActionType.OpenUrlNewTabAndSwitchState) ||
      component.actionType === ButtonActionType.SwitchState
    ) {
      if (component.actionValue) {
        const parsedStateId: number = +component.actionValue.split(',')[0];
        if (parsedStateId === stateId) {
          if (
            component.actionType === ButtonActionType.SwitchState &&
            !submitStateUsed &&
            component.subType === WidgetComponentSubType.ButtonSubmit
          ) {
            return true;
          } else {
            return false;
          }
        }
      }
    }
  }

  public checkIfStateIsInUseLeftSideBar(stateId: number, stateInUse: StatesInUse, widget?): any {
    let allUsageThisState = [];
    if (stateInUse.submitBtnState && +stateId === +stateInUse.submitBtnState?.linkedState) {
      allUsageThisState.push(stateInUse.submitBtnState);
    }
    if (stateInUse.alternativeState && +stateId === +stateInUse.alternativeState?.linkedState) {
      allUsageThisState.push(stateInUse.alternativeState);
    }
    if (
      stateInUse.regularBtnState?.length &&
      stateInUse.regularBtnState.some((state) => +state.linkedState === +stateId)
    ) {
      const regularBtnStates = stateInUse.regularBtnState.filter((state) => +state.linkedState === +stateId);
      allUsageThisState.push(...regularBtnStates);
    }
    this.getMissingLinkedStates(allUsageThisState, stateInUse, widget);
    return allUsageThisState;
  }

  public getDynamicUsedStatesLeftSideBar(action, usedStates, widget, languageService) {
    let copyUsedStates = JSON.parse(JSON.stringify(usedStates));
    if (
      action.action === ActionOnComponent.Edit &&
      (this.isRegularComponentAction(action.component) || WidgetComponentHelpers.isWidgetState(action.component))
    ) {
      this.getDynamicUsedStatesOnEdit(copyUsedStates, action, widget, languageService);
      if (this.itemHasActionValue(action.component)) {
        action.component.listItems.forEach((item) => {
          this.getDynamicUsedStatesOnEdit(copyUsedStates, action, widget, languageService, item);
        });
      } else if (action.component.type === WidgetComponentType.ButtonList) {
        copyUsedStates.regularBtnState = copyUsedStates.regularBtnState.filter(
          (state) => state.sourceComponentId !== action.component.id || !state.sourceItemId,
        );
      }
    } else if (action.action === ActionOnComponent.Remove && this.isRegularComponentAction(action.component)) {
      let targetUsedStateIdx =
        copyUsedStates.regularBtnState?.findIndex((usedState) => usedState.sourceComponentId === action.component.id) ??
        -1;
      if (targetUsedStateIdx !== -1) {
        copyUsedStates.regularBtnState.splice(targetUsedStateIdx, 1);
      }
      if (action.component.type === WidgetComponentType.ButtonList) {
        copyUsedStates.regularBtnState = copyUsedStates.regularBtnState.filter(
          (state) => state.sourceComponentId !== action.component.id,
        );
      }
    } else if (action.action === ActionOnComponent.Remove && WidgetComponentHelpers.isSubmitType(action.component)) {
      copyUsedStates.submitBtnState = null;
      copyUsedStates.alternativeState = null;
    } else if (action.action === ActionOnComponent.Remove && WidgetComponentHelpers.isWidgetState(action?.component)) {
      if (copyUsedStates.alternativeState?.sourceStateId === action.component.id) {
        copyUsedStates.alternativeState = null;
      }
      if (copyUsedStates.submitBtnState?.sourceStateId === action.component.id) {
        copyUsedStates.submitBtnState = null;
      }
      copyUsedStates.regularBtnState = copyUsedStates.regularBtnState?.filter(
        (state) => state.sourceStateId !== action.component.id,
      );
    }
    return copyUsedStates;
  }

  private getDynamicUsedStatesOnEdit(copyUsedStates, action, widget, languageService, el?) {
    let targetUsedStateIdx;
    targetUsedStateIdx =
      copyUsedStates.regularBtnState?.findIndex(
        (usedState) => usedState.sourceComponentId === action.component.id && !usedState.sourceItemId,
      ) ?? -1;
    if (el) {
      targetUsedStateIdx =
        copyUsedStates.regularBtnState?.findIndex(
          (usedState) => usedState.sourceComponentId === action.component.id && usedState.sourceItemId === el.id,
        ) ?? -1;
    }
    if (targetUsedStateIdx !== -1) {
      const actionValue = el ? el.action?.actionValue : action.component.actionValue;
      if (actionValue) {
        copyUsedStates.regularBtnState[targetUsedStateIdx].linkedState = this.getActionValueForRegularBtnState(
          action.component,
          el,
        );
        copyUsedStates.regularBtnState[targetUsedStateIdx].sourceText = this.getSourceTextForLinkedDialog(
          action.component,
          languageService,
          el,
        );
      } else {
        copyUsedStates.regularBtnState.splice(targetUsedStateIdx, 1);
      }
    } else {
      if (action.component.actionValue || el?.action?.actionValue) {
        let regularBtnState = {
          linkedState: this.getActionValueForRegularBtnState(action.component, el),
          sourceStateOrder:
            this.getStateAndComponentByComponentId(widget.states, action.component.id)?.targetState?.order + 1,
          sourceComponentId: action.component.id,
          sourceItemId: el?.id,
          sourceStateId: this.getStateAndComponentByComponentId(widget.states, action.component.id)?.targetState?.id,
          sourceText: this.getSourceTextForLinkedDialog(action.component, languageService, el),
        };
        (copyUsedStates.regularBtnState ??= []).push(regularBtnState);
      }
    }
    copyUsedStates.regularBtnState = copyUsedStates.regularBtnState?.filter((usedState: any) => {
      if (usedState.sourceComponentId !== action.component.id || !usedState.sourceItemId) {
        return true;
      }
      return action.component.listItems?.some((item: any) => item.id === usedState.sourceItemId);
    });
  }

  public getInitialAllUsedStatesLeftSideBar(widget: Widget, languageService): any {
    const allUsedStates: StatesInUse = {
      alternativeState: {} as LinkedState,
      submitBtnState: {} as LinkedState,
      regularBtnState: [],
    };
    if (widget && widget.states) {
      for (const state of widget.states) {
        if (state && state.alternativeStepId) {
          allUsedStates.alternativeState = {
            linkedState: state.alternativeStepId,
            sourceStateOrder: null,
            sourceStateId: state.id,
            sourceText: languageService.translateInstantly('campaign.widget.sidebar.layout.form-settings'),
          };
        }
        if (state && state.actionType && state.actionValue) {
          allUsedStates.regularBtnState.push({
            linkedState: +state.actionValue,
            sourceStateOrder: state.order + 1,
            sourceComponentId: state.id,
            sourceStateId: state.id,
            sourceText: this.getSourceTextForLinkedDialog(state, languageService),
          });
        }
        const floatingContainer = [];
        if (state.floatingContainer) {
          floatingContainer.push(JSON.parse(JSON.stringify(state.floatingContainer)));
        }
        if (state.containers) {
          for (const container of [...state.containers, ...floatingContainer]) {
            for (const component of container.components) {
              if (WidgetComponentHelpers.isSubmitType(component)) {
                if (component.actionValue) {
                  allUsedStates.submitBtnState = {
                    linkedState: component.actionValue,
                    sourceStateId: state.id,
                    sourceText: languageService.translateInstantly('campaign.widget.sidebar.layout.form-settings'),
                    sourceStateOrder: null,
                    sourceComponentId: component.id,
                  };
                }
                if (allUsedStates.alternativeState.linkedState) {
                  allUsedStates.alternativeState.sourceComponentId = component.id;
                }
                break;
              } else if (
                component.type === WidgetComponentType.Button ||
                component.type === WidgetComponentType.ButtonList
              ) {
                if (this.isRegularComponentAction(component) && component.actionValue) {
                  allUsedStates.regularBtnState.push(this.buildRegularStateInUse(component, state, languageService));
                }
                if (component.type === WidgetComponentType.ButtonList && this.itemHasActionValue(component)) {
                  component.listItems.forEach((item) => {
                    if (item.action?.actionValue) {
                      allUsedStates.regularBtnState.push(
                        this.buildRegularStateInUse(component, state, languageService, item),
                      );
                    }
                  });
                }
              }
              if (component.children) {
                for (const childComponent of component.children) {
                  if (childComponent.type === WidgetComponentType.Button || this.isRegularComponentAction(component)) {
                    if (WidgetComponentHelpers.isSubmitType(childComponent) && childComponent.actionValue) {
                      allUsedStates.submitBtnState = {
                        linkedState: component.actionValue,
                        sourceStateId: state.id,
                        sourceText: languageService.translateInstantly('campaign.widget.sidebar.layout.form-settings'),
                        sourceStateOrder: null,
                        sourceComponentId: component.id,
                      };
                    }
                    if (this.isRegularComponentAction(component) && childComponent.actionValue) {
                      allUsedStates.regularBtnState.push(
                        this.buildRegularStateInUse(component, state, languageService),
                      );
                    }
                    if (component.type === WidgetComponentType.ButtonList && this.itemHasActionValue(component)) {
                      component.listItems.forEach((item) => {
                        if (item.action?.actionValue) {
                          allUsedStates.regularBtnState.push(
                            this.buildRegularStateInUse(component, state, languageService, item),
                          );
                        }
                      });
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    return allUsedStates;
  }

  private buildRegularStateInUse(component, state, languageService, item?) {
    return {
      linkedState: this.getActionValueForRegularBtnState(component, item),
      sourceStateOrder: state.order + 1,
      sourceComponentId: component.id,
      sourceItemId: item?.id,
      sourceStateId: state.id,
      sourceText: this.getSourceTextForLinkedDialog(component, languageService, item),
    };
  }

  public getActionValueForRegularBtnState(component, item) {
    if (item) {
      return item.action?.actionValue;
    }
    return component.subType === WidgetComponentSubType.ButtonInstagramFollow
      ? component.actionValue.split(',')[0]
      : component.actionValue;
  }

  private itemHasActionValue(component) {
    return (
      component.type === WidgetComponentType.ButtonList &&
      component.listItems?.some((item) => item.action && item.action?.actionValue)
    );
  }

  public deleteLinkToState(statesInUse, widget, widgetState) {
    const copyStatesInUse = { ...statesInUse };
    const requests$ = [];
    const matchingStates =
      copyStatesInUse.regularBtnState?.filter((stateInUse) => +stateInUse.linkedState === widgetState.id) ?? [];
    const groupedByComponent = new Map<number, any[]>();
    matchingStates.forEach((stateInUse) => {
      const group = groupedByComponent.get(stateInUse.sourceComponentId) ?? [];
      group.push(stateInUse);
      groupedByComponent.set(stateInUse.sourceComponentId, group);
    });
    groupedByComponent.forEach((statesInUseGroup) => {
      this.deleteLinkFromButtons(statesInUseGroup, widget, requests$);
    });
    if (copyStatesInUse.alternativeState?.linkedState === widgetState.id) {
      const target = this.getStateAndComponentByComponentId(
        widget.states,
        copyStatesInUse.alternativeState.sourceComponentId,
      );

      const targetState = target.targetState;
      if (!targetState) return;
      targetState.alternativeStepApp = undefined;
      targetState.alternativeStepId = undefined;
      targetState.alternativeStepListId = undefined;
      targetState.subscriberCheckApp = null;
      targetState.subscriberCheckListId = null;
      targetState.subscriberCheckStateId = null;
      requests$.push(this.widgetService.updateWidgetState(widget.id, targetState));
    }
    if (+copyStatesInUse.submitBtnState?.linkedState === widgetState.id) {
      this.deleteLinkFromButtons([copyStatesInUse.submitBtnState], widget, requests$);
    }
    return requests$;
  }

  private deleteLinkFromButtons(statesInUseGroup: any[], widget, requests$) {
    const sourceComponentId = statesInUseGroup[0].sourceComponentId;
    const target = this.getStateAndComponentByComponentId(widget.states, sourceComponentId);
    const { targetContainer, targetState, targetComponent } = target;
    if (!targetComponent || !targetState || !targetContainer) return;
    statesInUseGroup.forEach((stateInUse) => {
      if (stateInUse.sourceItemId) {
        const targetItem = targetComponent.listItems?.find((item: any) => item.id === stateInUse.sourceItemId);
        if (targetItem?.action) {
          targetItem.action.actionValue = null;
          targetItem.action.actionType = null;
        }
      } else {
        targetComponent.actionValue = '';
        targetComponent.actionType = ButtonActionType.CloseWidget;
      }
    });
    requests$.push(this.widgetEditorDataService.updateWidgetComponentObservable(targetComponent, widget));
  }

  private isRegularComponentAction(component) {
    return (
      component.subType === WidgetComponentSubType.ButtonRegular ||
      component.type === WidgetComponentType.ButtonList ||
      component.subType === WidgetComponentSubType.ButtonHide ||
      component.subType === WidgetComponentSubType.ButtonInstagramFollow
    );
  }

  private getSourceTextForLinkedDialog(component, languageService, item?): string {
    const componentTitle = WidgetComponentHelpers.getWidgetComponentTitle(component);
    const translatedComponentTitle = componentTitle ? languageService.translateInstantly(componentTitle) : '';
    return (
      translatedComponentTitle +
      (component.textProperties?.text && component.subType === WidgetComponentSubType.ButtonRegular
        ? ':&nbsp;' + component.textProperties.text
        : item
          ? `(${item.value})`
          : '')
    );
  }

  getMissingLinkedStates(allUsageThisState: any[], stateInUse: any, widget?): void {
    const allUsageSet = new Set(allUsageThisState.map((a) => a.linkedState));
    const missedStates: { key: string; linkedState: number }[] = [];
    let statesIdSet;
    if (!widget) return;
    if (widget.states?.length) {
      statesIdSet = new Set(widget.states.map((state) => state.id.toString()));
    }
    Object.entries(stateInUse).forEach(([key, value]) => {
      const items = Array.isArray(value) ? value : [value];
      items.forEach((item) => {
        if (
          item?.linkedState != null &&
          !allUsageSet.has(item.linkedState) &&
          (!statesIdSet.has(item.linkedState) || !statesIdSet)
        ) {
          missedStates.push({ key, linkedState: item.linkedState });
        }
      });
    });
    if (missedStates.length) {
      missedStates.forEach((state) => {
        this.logger.warn(
          `Component(${state.key}) on widget has actionValue with non-existed state id - ${state.linkedState}`,
        );
      });
    }
  }

  //stateUsed for old editor
  public getAllUsedStates(widget: Widget): any {
    const allUsedStates = {
      alternativeStateUsed: 0,
      submitStateUsed: 0,
      regularStatesUsed: [],
    };
    if (widget && widget.states) {
      for (const state of widget.states) {
        if (state && state.alternativeStepId) {
          allUsedStates.alternativeStateUsed = state.alternativeStepId;
        }
        if (state.containers) {
          for (const container of state.containers) {
            for (const component of container.components) {
              if (component.type === WidgetComponentType.Button || component.type === WidgetComponentType.ButtonList) {
                if (WidgetComponentHelpers.isSubmitType(component) && component.actionValue) {
                  allUsedStates.submitStateUsed = component.actionValue;
                }
                if (
                  (component.subType === WidgetComponentSubType.ButtonRegular ||
                    component.subType === WidgetComponentSubType.ButtonListRadio ||
                    component.subType === WidgetComponentSubType.ButtonListCheckbox) &&
                  component.actionValue
                ) {
                  allUsedStates.regularStatesUsed.push({
                    actionValue: component.actionValue,
                    componentId: component.id,
                  });
                }
              }
              if (component.children) {
                for (const childComponent of component.children) {
                  if (
                    childComponent.type === WidgetComponentType.Button ||
                    component.type === WidgetComponentType.ButtonList
                  ) {
                    if (WidgetComponentHelpers.isSubmitType(childComponent) && childComponent.actionValue) {
                      allUsedStates.submitStateUsed = component.actionValue;
                    }
                    if (
                      (childComponent.subType === WidgetComponentSubType.ButtonRegular ||
                        component.subType === WidgetComponentSubType.ButtonListRadio ||
                        component.subType === WidgetComponentSubType.ButtonListCheckbox) &&
                      childComponent.actionValue
                    ) {
                      allUsedStates.regularStatesUsed.push({
                        actionValue: childComponent.actionValue,
                        componentId: childComponent.id,
                      });
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    return allUsedStates;
  }

  public static updateStateRenderableProperties(destState: WidgetState, sourceState: WidgetState) {
    if (destState.type === sourceState.type) {
      destState.position = sourceState.position;
      destState.width = sourceState.width;
      destState.height = sourceState.height;
    }
    destState.radius = sourceState.radius;
    destState.border = sourceState.border;
    destState.background = sourceState.background;
    destState.padding = sourceState.padding;
    destState.margin = sourceState.margin;
    destState.shadow = sourceState.shadow;
    destState.actionType = sourceState.actionType;
    destState.actionValue = sourceState.actionValue;
    destState.actionScript = sourceState.actionScript;
    destState.actionTriggerDelay = sourceState.actionTriggerDelay;
    // destState.order = sourceState.order;
    destState.automationId = sourceState.automationId;
    // refreshStateInWidget Object.assigns this state over the just-saved one, so anything omitted here reverts.
    destState.alternativeStepApp = sourceState.alternativeStepApp;
    destState.alternativeStepListId = sourceState.alternativeStepListId;
    destState.alternativeStepId = sourceState.alternativeStepId;
    destState.mobile = sourceState.mobile;
    destState.direction = sourceState.direction;
    destState.rotation = sourceState.rotation;
    destState.wcagProperties = sourceState.wcagProperties;
    destState.productTourProperties = sourceState.productTourProperties;
  }
}
